import { useState, useEffect, useMemo } from 'react'
import {
  useQueryClient,
  QueryObserver,
  QueryObserverOptions,
  QueryFunction,
} from '@tanstack/react-query'
import isEmpty from 'lodash/isEmpty'
import { isFunctionWithSignature } from '@global/utils/typeGuards'

type QueryKey = readonly unknown[]

type CustomQueryObserverOptions<TQueryFnData, TError, TData, TQueryKey extends QueryKey> =
  | Omit<QueryObserverOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>
  | {
      // Ensure select data CAN have a type of the Data not just its default QueryKey,
      // OR is necessary to allow this type.
      select?: (data: TQueryFnData) => TData
      staleTime?: number
      refetchOnWindowFocus?: boolean
    }

interface ObserverArgs<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> {
  queryFn: QueryFunction<TQueryFnData>
  id: string | number
  options?: CustomQueryObserverOptions<TQueryFnData, TError, TData, TQueryKey>
}

interface IState<T> {
  data: T
  isLoading: boolean
}

/**
 * Creates a generic factory pattern for building custom observer hooks.
 *
 * @template TQueryFnData The type of data returned by the query function.
 * @template TError The type of error that can occur.
 * @template TData The type of the final data after transformation (defaults to TQueryFnData if no transformation occurs).
 * @template TQueryKey The type of the query key (extends QueryKey).
 *
 * @param {string} baseKey The base key for the React Query cache.
 *
 * @returns {function} A custom hook factory for observing the React Query cache.
 *
 * @example
 * const useListObserver = createObserverHook<ListDTOResponse, Error, ListTableTransformData>('custom_cache_key')
 *
 * // Component usage:
 * const listData = useListObserver({
 *   queryFn: fetchList,
 *   id: listId,
 *   options: {
 *     select: (data) => transformListData(data),
 *   },
 * })
 */
export function createObserverHook<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(baseKey: string) {
  /**
   * Creates a custom hook for observing the React Query cache.
   * @param {{}} args
   * @param {QueryFunction} args.queryFn The useQuery function from React Query.
   * @param {string | number} args.id The query cache key.
   * @param {QueryObserverOptions} args.options The useQuery options.
   */
  return function useDataObserver({
    queryFn,
    id,
    options,
  }: ObserverArgs<TQueryFnData, TError, TData, TQueryKey>) {
    const queryClient = useQueryClient()
    const queryKey = useMemo(() => [baseKey, id] as QueryKey, [baseKey, id])

    const [state, setState] = useState<IState<TData | undefined>>({
      data: queryClient.getQueryData<TData>(queryKey),
      isLoading: true,
    })

    const { data, isLoading } = state

    const memoizedOptions = useMemo(() => options, [JSON.stringify(options)])

    if (options?.select) {
      try {
        isFunctionWithSignature(options.select)
      } catch (error) {
        if (error instanceof TypeError) {
          throw new TypeError(`Invalid select option in useDataObserver: ${error.message}`)
        } else if (error instanceof Error) {
          throw new Error(
            `Unexpected error with select option in useDataObserver: ${error.message}`,
          )
        } else {
          throw new Error(`Unknown error occurred with select option in useDataObserver`)
        }
      }
    }

    useEffect(() => {
      const { select, ...restOptions } = options ?? {}
      const observerOptions: QueryObserverOptions<TQueryFnData, TError, TData, TQueryKey> = {
        queryKey,
        queryFn,
        staleTime: options?.staleTime ?? Infinity,
        refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
        ...restOptions,
        select: select as unknown as QueryObserverOptions<
          TQueryFnData,
          TError,
          TData,
          TQueryKey
        >['select'],
      }

      const observer = new QueryObserver(queryClient, observerOptions)

      const unsubscribe = observer.subscribe(result => {
        if (typeof result.data === 'undefined' || isEmpty(result.data)) return

        setState(prev => ({ ...prev, data: result.data, isLoading: false }))
      })

      return () => {
        unsubscribe()
      }
    }, [queryClient, queryKey, queryFn, memoizedOptions])

    return {
      data,
      isLoading,
    }
  }
}
